/**
 * Canonical enumerations and the slug normaliser.
 *
 * The shipped seed data spells the same value several ways — `VeryHigh` against
 * `Very High`, `hybrid` against `Hybrid`. Every comparison in the system goes
 * through `slug()` so a casing difference can never silently drop a 1.30
 * multiplier or fail to derive a hybrid identity workstream.
 * See normalisations N1, N2 and N12 in docs/DEMO_DECISIONS.md.
 */
/** Lower-case, strip punctuation, collapse whitespace and separators to `_`. */
export function slug(value) {
    return value
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // VeryHigh -> Very_High
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}
/** Look a value up in a record by canonical slug rather than exact spelling. */
export function bySlug(table, key) {
    const wanted = slug(key);
    for (const [k, v] of Object.entries(table)) {
        if (slug(k) === wanted)
            return v;
    }
    return undefined;
}
export const PIPELINE_STAGES = [
    'lead',
    'qualifying',
    'scoping',
    'quoted',
    'negotiating',
    'contracting',
    'closed_won',
    'closed_lost',
];
/**
 * Legal stage moves. The source pack never defined these; without a matrix the
 * `POST /stage-transitions` route and its "invalid transitions fail" acceptance
 * check cannot be written. Forward movement is free, backward movement is allowed
 * one step for correction, and the closed states are terminal unless reopened
 * explicitly to Qualifying.
 */
export const STAGE_TRANSITIONS = {
    lead: ['qualifying', 'closed_lost'],
    qualifying: ['lead', 'scoping', 'closed_lost'],
    scoping: ['qualifying', 'quoted', 'closed_lost'],
    quoted: ['scoping', 'negotiating', 'closed_won', 'closed_lost'],
    negotiating: ['quoted', 'contracting', 'closed_won', 'closed_lost'],
    contracting: ['negotiating', 'closed_won', 'closed_lost'],
    closed_won: [],
    closed_lost: ['qualifying'],
};
/** The probability a stage carries, in basis points. A stage move resets it to this. */
export const STAGE_PROBABILITY_BP = {
    lead: 500, qualifying: 1500, scoping: 3000, quoted: 5000,
    negotiating: 7000, contracting: 9000, closed_won: 10000, closed_lost: 0,
};
export function canTransition(from, to) {
    return STAGE_TRANSITIONS[from].includes(to);
}
export const QUOTE_STATUSES = [
    'draft',
    'pending_approval',
    'approved',
    'issued',
    'accepted',
    'superseded',
    'declined',
    'withdrawn',
];
/**
 * A version is mutable only while it is a working draft. Everything else is
 * frozen, which is what makes the content hash and the approval binding mean
 * anything. Resolves the contradiction between 05 line 163 and line 438.
 */
export function isMutableStatus(status) {
    return status === 'draft';
}
export const QUOTE_STATUS_TRANSITIONS = {
    draft: ['pending_approval', 'approved', 'withdrawn'],
    pending_approval: ['approved', 'draft', 'withdrawn'],
    approved: ['issued', 'draft', 'withdrawn'],
    issued: ['accepted', 'declined', 'superseded', 'withdrawn'],
    accepted: ['superseded'],
    superseded: [],
    declined: [],
    withdrawn: [],
};
export const ROLES = [
    'commercial_administrator',
    'commercial_user',
    'engineer',
    'approver',
    'read_only',
];
export const ROLE_LABELS = {
    commercial_administrator: 'Commercial Administrator',
    commercial_user: 'Commercial User',
    engineer: 'Engineer',
    approver: 'Approver',
    read_only: 'Read Only',
};
/**
 * Cost and margin visibility. The plan flagged the Engineer role as undecided;
 * the demo grants Engineers cost visibility because they own estimation notes and
 * cannot sanity-check an estimate without it, and withholds it from Read Only.
 */
export const ROLES_WITH_COST_VISIBILITY = [
    'commercial_administrator',
    'commercial_user',
    'engineer',
    'approver',
];
export function canSeeCost(role) {
    return ROLES_WITH_COST_VISIBILITY.includes(role);
}
/** The twelve governed categories: seven numbered, four modules, plus design. */
export const CATEGORIES = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    'M1',
    'M2',
    'M3',
    'M4',
    'DES',
];
/**
 * One short name per category, shared by the API and the web app.
 *
 * It lived in the web formatter alone, so the moment the server had to name a
 * category — explaining why the scope derived the one it did — there was nowhere
 * to read it from.
 */
export const CATEGORY_SHORT = {
    '1': 'Pool of hours',
    '2': 'Retainer',
    '3': 'Small works',
    '4': 'Audit',
    '5': 'Project',
    '6': 'Multi-country',
    '7': 'Regulated',
    M1: 'Procurement',
    M2: 'Staffing',
    M3: 'Tender',
    M4: 'Change',
    DES: 'Design',
};
/** "Category 2 — Retainer", or "M1 — Procurement". */
export function categoryTitle(key) {
    if (!key)
        return '—';
    const short = CATEGORY_SHORT[key] ?? key;
    return `${/^[1-7]$/.test(key) ? `Category ${key}` : key} — ${short}`;
}
export const SERVICE_LINES = ['MSV', 'PRJ', 'ADV', 'DES', 'PRC', 'RES'];
export const GRADES = ['M1', 'M2', 'M3', 'M4', 'C1', 'C2', 'C3', 'C4'];
export const LOADING_WINDOWS = [
    'normal',
    'evening',
    'saturday',
    'sunday',
    'public_holiday',
    'overnight',
];
export const COMPLEXITY_LEVELS = ['low', 'standard', 'high', 'very_high'];
export const SLA_LEVELS = ['standard', 'enhanced', 'priority'];
export const PROACTIVE_LEVELS = ['co_managed', 'standard', 'premium', 'regulated'];
export const SERVICE_TIERS = ['triage', 'essential', 'standard', 'premium'];
/**
 * How a line's money is recognised. The four-totals invariant is mandated
 * everywhere and specified nowhere, so the demo defines it here: every line
 * declares its frequency, and the four totals are derived from that alone.
 */
export const LINE_FREQUENCIES = ['one_off', 'monthly', 'annual'];
export const ITEM_TYPES = ['hardware', 'software', 'subscription', 'service', 'other'];
/**
 * How a line's sell price is formed. `benchmark_effort` is rate x effort and is
 * the reason the universal "sell price is always a formula from cost" principle
 * could never hold literally — the handbook scopes that rule to hardware.
 */
export const PRICING_TREATMENTS = [
    'benchmark_effort',
    'tier_formula',
    'gross_margin',
    'markup',
    'pass_through',
    'subcontract',
    'fixed_price',
];
/**
 * `estimated` is a figure the system assigned so a quote does not go blank,
 * pending a PTS engineer's confirmation. It prices, and every quote that uses
 * one says so.
 */
export const BENCHMARK_STATUSES = ['confirmed', 'estimated', 'proposed', 'subcontract'];
export const CONFIDENCE_LEVELS = ['high', 'medium', 'low', 'none'];
export const RELEASE_STATUSES = ['draft', 'validating', 'published', 'retired'];
export const CURRENCIES = ['HKD', 'USD', 'CNY', 'SGD', 'GBP', 'AUD', 'JPY', 'TWD'];
export const INVOICING_ENTITIES = ['PTS HK', 'PTS SG', 'Peng Tu Shanghai'];
export const CLIENT_STATUSES = ['prospect', 'active', 'dormant', 'former', 'internal'];
export const CLOSED_LOST_REASONS = [
    { key: 'price_cheaper_provider', label: 'Price, lost to a cheaper provider', requiresCompetitor: false, requiresWinningPrice: true },
    { key: 'price_budget_withdrawn', label: 'Price, client budget withdrawn or reduced', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'lost_to_incumbent', label: 'Lost to incumbent, no change made', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'lost_to_named_competitor', label: 'Lost to a named competitor', requiresCompetitor: true, requiresWinningPrice: false },
    { key: 'scope_changed', label: 'Scope changed, no longer required', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'went_in_house', label: 'Client went in-house', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'timing_deferred', label: 'Timing, deferred beyond this cycle', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'pts_declined', label: 'PTS declined to bid', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'no_decision', label: 'No decision, went quiet', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'lost_on_capability', label: 'Lost on capability or reference', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'lost_on_geography', label: 'Lost on geography or coverage', requiresCompetitor: false, requiresWinningPrice: false },
    { key: 'lost_on_terms', label: 'Lost on commercial terms, not price', requiresCompetitor: false, requiresWinningPrice: false },
];
//# sourceMappingURL=enums.js.map